import { fetchEntityWithCache } from "../../../helper/calendarCache";

const COLOR_MODE_KEY = 'calendar_color_mode';
const DEFAULT_CALENDAR_KEY_PREFIX = 'calendar_default_id_';

const state = {
  calendars: {
    data: [],
    uiFlags: { isFetching: false },
  },
  events: {
    data: [],
    uiFlags: { isFetching: false },
  },
  connectedAccounts: [],
  isSyncing: false,
  syncTimeout: null,
  syncCompletedAt: null,
  reauthRequiredHookIds: [],
  colorMode: localStorage.getItem(COLOR_MODE_KEY) || 'subtle',
  defaultCalendarId: null,
};

const getters = {
  getCalendars: (_state) => {
    if (_state.connectedAccounts.length > 0) {
      const validHookIds = _state.connectedAccounts.map((a) => a.id);
      return _state.calendars.data.filter((c) => validHookIds.includes(c.hook_id));
    }
    return _state.calendars.data;
  },
  getEvents: (_state) => _state.events.data,
  getSelectedCalendarIds: (_state, _getters) => {
    return _getters.getCalendars.filter((c) => c.selected).map((c) => c.id);
  },
  getUIFlags: (_state) => ({
    isLoadingCalendars: _state.calendars.uiFlags.isFetching,
    isLoadingEvents: _state.events.uiFlags.isFetching,
  }),
  getConnectedAccounts: (_state) => _state.connectedAccounts,
  getIsSyncing: (_state) => _state.isSyncing,
  getColorMode: (_state) => _state.colorMode,
  getSelectedCount: (_state, _getters) => {
    return _getters.getCalendars.filter((c) => c.selected).length;
  },
  getDefaultCalendarId: (_state, _getters) => {
    const calendars = _getters.getCalendars || [];
    if (!calendars.length) return null;

    if (_state.defaultCalendarId) {
      const exists = calendars.some(c => c.id === _state.defaultCalendarId);
      if (exists) return _state.defaultCalendarId;
    }

    const primary = calendars.find(c => c.is_default);
    if (primary) return primary.id;

    const selected = calendars.find(c => c.selected);
    if (selected) return selected.id;

    return calendars[0]?.id || null;
  },
  getCanManageSettings: (_state, _getters, rootState, rootGetters) => {
    if (rootGetters.getCurrentRole === 'administrator') return true;

    // Fallback sem route params: verifica diretamente no auth state.
    // getCurrentRole depende de rootState.route.params.accountId que pode
    // não estar disponível no mount inicial (timing issue).
    const accountId = Number(rootState.route?.params?.accountId);
    const accounts = rootState.auth?.currentUser?.accounts || [];
    const account = accountId
      ? accounts.find(a => Number(a.id) === accountId)
      : accounts.find(a => a.role === 'administrator');
    const perms = account?.permissions || [];

    if (account?.role === 'administrator') return true;
    if (perms.includes('calendar.manage')) return true;

    return (_getters.getCalendars || []).some(c => c.can_manage_settings);
  },
  getSyncCompletedAt: (_state) => _state.syncCompletedAt,
  getReauthRequiredHookIds: (_state) => _state.reauthRequiredHookIds,
  getCreatableCalendarIds: (_state, _getters) => {
    return (_getters.getCalendars || [])
      .filter(c => c.can_create_events)
      .map(c => c.id);
  },
};

const actions = {
  fetchCalendars: async ({ commit, dispatch, rootState }, { force = false } = {}) => {
    const { accountId } = rootState.route.params;
    commit("SET_CALENDARS_UI_FLAG", { isFetching: true });
    try {
      const response = await fetchEntityWithCache(
        "calendars",
        () => window.axios.get(`/api/v1/accounts/${accountId}/calendars`),
        { force },
      );
      commit("SET_CALENDARS", response.data);
      await dispatch("initializeDefaultCalendar");
    } catch (error) {
      // handle error
    } finally {
      commit("SET_CALENDARS_UI_FLAG", { isFetching: false });
    }
  },
  fetchEvents: async ({ commit, rootState }, { start, end, force = false }) => {
    const { accountId } = rootState.route.params;
    commit("SET_EVENTS_UI_FLAG", { isFetching: true });
    try {
      const response = await fetchEntityWithCache(
        `events-${start}-${end}`,
        () =>
          window.axios.get(`/api/v1/accounts/${accountId}/calendar_events`, {
            params: { start, end },
          }),
        { force },
      );
      commit("SET_EVENTS", response.data);
    } finally {
      commit("SET_EVENTS_UI_FLAG", { isFetching: false });
    }
  },
  toggleCalendarSelection: async ({ commit, rootState }, calendarId) => {
    const { accountId } = rootState.route.params;
    const response = await window.axios.post(
      `/api/v1/accounts/${accountId}/calendars/${calendarId}/toggle_selection`,
    );
    commit("UPDATE_CALENDAR", response.data);
  },
  createEvent: async ({ commit, rootState }, eventData) => {
    const { accountId } = rootState.route.params;
    const response = await window.axios.post(
      `/api/v1/accounts/${accountId}/calendar_events`,
      { event: eventData }
    );
    commit("ADD_OR_UPDATE_EVENT", response.data);
    return response.data;
  },
  updateEvent: async ({ commit, rootState }, eventData) => {
    const { accountId } = rootState.route.params;
    const response = await window.axios.patch(
      `/api/v1/accounts/${accountId}/calendar_events/${eventData.id}`,
      { event: eventData }
    );
    commit("ADD_OR_UPDATE_EVENT", response.data);
    return response.data;
  },
  deleteEvent: async ({ commit, rootState }, eventId) => {
    const { accountId } = rootState.route.params;
    await window.axios.delete(
      `/api/v1/accounts/${accountId}/calendar_events/${eventId}`
    );
    commit("REMOVE_EVENT", eventId);
  },
  addOrUpdateEvent: ({ commit }, eventData) => {
    commit("ADD_OR_UPDATE_EVENT", eventData);
  },
  removeEvent: ({ commit }, eventId) => {
    commit("REMOVE_EVENT", eventId);
  },
  fetchConnectedAccounts: async ({ commit, rootState }) => {
    const { accountId } = rootState.route.params;
    try {
      const response = await window.axios.get(
        `/api/v1/accounts/${accountId}/calendars/connected_accounts`
      );
      commit("SET_CONNECTED_ACCOUNTS", response.data);
    } catch (error) {
      // handle error
    }
  },
  syncAll: async ({ commit, rootState: rs }) => {
    const { accountId } = rs.route.params;
    commit("SET_SYNCING", true);
    // Safety: reset after 90s if ActionCable event never arrives
    const t = setTimeout(() => commit("SET_SYNCING", false), 90000);
    commit("SET_SYNC_TIMEOUT", t);
    try {
      await window.axios.post(`/api/v1/accounts/${accountId}/calendars/sync`);
    } catch (error) {
      clearTimeout(t);
      commit("SET_SYNC_TIMEOUT", null);
      commit("SET_SYNCING", false);
    }
  },
  syncAccount: async ({ commit, rootState: rs }, hookId) => {
    const { accountId } = rs.route.params;
    commit("SET_SYNCING", true);
    const t = setTimeout(() => commit("SET_SYNCING", false), 90000);
    commit("SET_SYNC_TIMEOUT", t);
    try {
      await window.axios.post(
        `/api/v1/accounts/${accountId}/calendars/sync`,
        null,
        { params: { hook_id: hookId } }
      );
    } catch (error) {
      clearTimeout(t);
      commit("SET_SYNC_TIMEOUT", null);
      commit("SET_SYNCING", false);
    }
  },
  onSyncCompleted: async ({ commit, dispatch, state: s }) => {
    clearTimeout(s.syncTimeout);
    commit("SET_SYNC_TIMEOUT", null);
    commit("SET_SYNCING", false);
    await dispatch("fetchCalendars", { force: true });
    commit("SET_SYNC_COMPLETED_AT", Date.now());
  },
  onSyncFailed: ({ commit, state: s }) => {
    clearTimeout(s.syncTimeout);
    commit("SET_SYNC_TIMEOUT", null);
    commit("SET_SYNCING", false);
  },
  onReauthRequired: ({ commit, state: s }, data) => {
    clearTimeout(s.syncTimeout);
    commit("SET_SYNC_TIMEOUT", null);
    commit("SET_SYNCING", false);
    if (data?.hook_id) commit("ADD_REAUTH_REQUIRED", data.hook_id);
  },
  clearReauthRequired: ({ commit }, hookId) => {
    commit("REMOVE_REAUTH_REQUIRED", hookId);
  },
  disconnectAccount: async ({ dispatch, rootState }, hookId) => {
    const { accountId } = rootState.route.params;
    await window.axios.delete(
      `/api/v1/accounts/${accountId}/calendars/disconnect`,
      { params: { hook_id: hookId } }
    );
    await dispatch("fetchConnectedAccounts");
    await dispatch("fetchCalendars", { force: true });
  },
  setColorMode: ({ commit }, mode) => {
    localStorage.setItem(COLOR_MODE_KEY, mode);
    commit("SET_COLOR_MODE", mode);
  },
  // eslint-disable-next-line no-shadow
  initializeDefaultCalendar: ({ commit, rootState, getters }) => {
    const { accountId } = rootState.route.params;
    const storageKey = `${DEFAULT_CALENDAR_KEY_PREFIX}${accountId}`;
    const fromStorage = localStorage.getItem(storageKey);
    const parsed = fromStorage ? Number(fromStorage) : null;
    const calendars = getters.getCalendars || [];

    const hasParsed = parsed && calendars.some(c => c.id === parsed);
    if (hasParsed) {
      commit("SET_DEFAULT_CALENDAR_ID", parsed);
      return;
    }

    const fallbackId = getters.getDefaultCalendarId;
    commit("SET_DEFAULT_CALENDAR_ID", fallbackId);
    if (fallbackId) {
      localStorage.setItem(storageKey, String(fallbackId));
    } else {
      localStorage.removeItem(storageKey);
    }
  },
  setDefaultCalendar: ({ commit, rootState }, calendarId) => {
    const { accountId } = rootState.route.params;
    const parsed = calendarId ? Number(calendarId) : null;
    const storageKey = `${DEFAULT_CALENDAR_KEY_PREFIX}${accountId}`;

    commit("SET_DEFAULT_CALENDAR_ID", parsed);
    if (parsed) {
      localStorage.setItem(storageKey, String(parsed));
    } else {
      localStorage.removeItem(storageKey);
    }
  },
};

const mutations = {
  SET_CALENDARS: (_state, data) => {
    _state.calendars.data = data;
  },
  SET_EVENTS: (_state, data) => {
    _state.events.data = data;
  },
  SET_CALENDARS_UI_FLAG: (_state, flags) => {
    _state.calendars.uiFlags = { ..._state.calendars.uiFlags, ...flags };
  },
  SET_EVENTS_UI_FLAG: (_state, flags) => {
    _state.events.uiFlags = { ..._state.events.uiFlags, ...flags };
  },
  UPDATE_CALENDAR: (_state, data) => {
    const index = _state.calendars.data.findIndex((c) => c.id === data.id);
    if (index !== -1) _state.calendars.data.splice(index, 1, data);
  },
  ADD_OR_UPDATE_EVENT: (_state, data) => {
    const index = _state.events.data.findIndex((e) => e.id === data.id);
    if (index !== -1) _state.events.data.splice(index, 1, data);
    else _state.events.data.push(data);
  },
  REMOVE_EVENT: (_state, eventId) => {
    _state.events.data = _state.events.data.filter((e) => e.id !== eventId);
  },
  SET_CONNECTED_ACCOUNTS: (_state, data) => {
    _state.connectedAccounts = data;
  },
  SET_SYNCING: (_state, val) => {
    _state.isSyncing = val;
  },
  SET_SYNC_TIMEOUT: (_state, val) => {
    _state.syncTimeout = val;
  },
  SET_SYNC_COMPLETED_AT: (_state, val) => {
    _state.syncCompletedAt = val;
  },
  ADD_REAUTH_REQUIRED: (_state, hookId) => {
    if (!_state.reauthRequiredHookIds.includes(hookId)) {
      _state.reauthRequiredHookIds.push(hookId);
    }
  },
  REMOVE_REAUTH_REQUIRED: (_state, hookId) => {
    _state.reauthRequiredHookIds = _state.reauthRequiredHookIds.filter(id => id !== hookId);
  },
  SET_COLOR_MODE: (_state, mode) => {
    _state.colorMode = mode;
  },
  SET_DEFAULT_CALENDAR_ID: (_state, calendarId) => {
    _state.defaultCalendarId = calendarId;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
